import { supabase } from './supabase';
import {
  buildInventoryByCategory,
  monthRangeIso,
  type InventoryCategoryGroup,
} from './inventoryCategoryReport';

interface ConsumedTransactionRow {
  quantity: number;
  stock_items: {
    id: string;
    name: string;
    unit: string;
    acquisition_cost: number;
    category_id: string | null;
    category: { id: string; name: string } | null;
  };
}

/** Internal withdrawals this month grouped by stock category (valued at acquisition cost). */
export async function fetchInventoryConsumedByCategory(
  businessId: string
): Promise<InventoryCategoryGroup[]> {
  if (!supabase) throw new Error('Database connection not available');

  const { start, end } = monthRangeIso();
  const { data, error } = await supabase
    .from('stock_transactions')
    .select(
      `
      quantity,
      stock_items!inner(
        id,
        name,
        unit,
        acquisition_cost,
        category_id,
        business_id,
        category:stock_categories(
          id,
          name
        )
      )
    `
    )
    .eq('type', 'sale')
    .is('product_sale_id', null)
    .eq('stock_items.business_id', businessId)
    .gte('created_at', start)
    .lte('created_at', end);

  if (error) throw error;

  const entries = ((data ?? []) as ConsumedTransactionRow[]).flatMap((row) => {
    const item = row.stock_items;
    if (!item) return [];

    const qty = Math.abs(Number(row.quantity) || 0);
    if (qty <= 0) return [];

    return [
      {
        itemId: item.id,
        name: item.name,
        unit: item.unit,
        quantity: qty,
        amount: qty * (Number(item.acquisition_cost) || 0),
        categoryId: item.category_id,
        categoryName: item.category?.name ?? 'Uncategorized',
      },
    ];
  });

  return buildInventoryByCategory(entries);
}

export type { InventoryCategoryGroup };
